import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { colors } from '../styles/colors';

const MentorContainer = styled.section`
  padding: 80px 0;
  background: linear-gradient(180deg, ${colors.backgroundSecondary} 0%, white 100%);
  overflow: hidden;
`;

const MentorContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
  text-align: center;
`;

const SectionTitle = styled.h2`
  font-size: 48px;
  font-weight: 700;
  color: ${colors.textPrimary};
  margin-bottom: 40px;
  line-height: 1.2;
  
  @media (max-width: 768px) {
    font-size: 36px;
    margin-bottom: 32px;
  }
  
  .desktop-text {
    @media (max-width: 768px) {
      display: none;
    }
  }
  
  .mobile-text {
    @media (min-width: 769px) {
      display: none;
    }
  }
`;

const SectionSubtitle = styled.p`
  font-size: 18px;
  color: ${colors.textSecondary};
  margin-bottom: 48px;
  line-height: 1.6;
`;

// 데스크탑용 가로 스크롤 컨테이너
const DesktopCarousel = styled.div`
  position: relative;
  overflow: hidden;
  margin-bottom: 32px;
  display: flex;
  justify-content: center;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const DesktopCardsContainer = styled.div<{ current: number; cardWidth: number; cardGap: number }>`
  display: flex;
  gap: 32px;
  transition: transform 0.4s ease;
  transform: translateX(calc(50% - ${p => (p.current + 9) * (p.cardWidth + p.cardGap)}px - ${p => p.cardWidth / 2}px));
  padding: 20px 0;
  justify-content: flex-start;
  align-items: center;
`;

const DesktopCard = styled.div`
  flex-shrink: 0;
  width: 320px;
  height: 420px;
  background: white;
  border-radius: 24px;
  box-shadow: 0 16px 48px rgba(0, 0, 0, 0.12);
  border: 1px solid ${colors.borderLight};
  display: flex;
  flex-direction: column;
  overflow: hidden;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  
  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 24px 60px rgba(0, 0, 0, 0.18);
  }
`;

// 모바일용 스택 컨테이너  
const MobileStack = styled.div`
  position: relative;
  height: 520px;
  margin: 0 auto 32px;
  max-width: 100%;
  padding: 0 20px;
  
  @media (min-width: 769px) {
    display: none;
  }
  
  @media (max-width: 350px) {
    padding: 0 10px;
    height: 480px;
  }
`;

const MobileCardsStage = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  perspective: 1000px;
`;

const MobileCard = styled.div<{ translateX: number; translateY: number; scale: number; z: number; opacity: number }>`
  position: absolute;
  top: 50%;
  left: 50%;
  width: min(300px, calc(100vw - 40px));
  height: 440px;
  transform: translate(-50%, -50%) translateX(${p => p.translateX}px) translateY(${p => p.translateY}px) scale(${p => p.scale});
  z-index: ${p => p.z};
  opacity: ${p => p.opacity};
  background: white;
  border-radius: 24px;
  box-shadow: 0 16px 48px rgba(0, 0, 0, 0.12);
  border: 1px solid ${colors.borderLight};
  transition: transform 280ms ease, opacity 220ms ease, box-shadow 280ms ease;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  
  @media (max-width: 350px) {
    width: min(280px, calc(100vw - 20px));
    height: 400px;
  }
`;

const MentorMedia = styled.div`
  position: relative;
  height: 55%;
  background: linear-gradient(135deg, ${colors.primary}, ${colors.primaryLight});
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`;

const PortraitFallback = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 48px;
  font-weight: 800;
  letter-spacing: 1px;
  
  @media (max-width: 350px) {
    font-size: 40px;
  }
`;

const MentorBody = styled.div`
  flex: 1;
  padding: 20px 20px 16px;
  display: flex;
  flex-direction: column;
`;

const MentorName = styled.h3`
  font-size: 22px;
  font-weight: 800;
  color: ${colors.textPrimary};
  margin-bottom: 6px;
  text-align: left;
  
  @media (max-width: 350px) {
    font-size: 20px;
  }
`;

const MentorTitle = styled.p`
  font-size: 13px;
  color: ${colors.primary};
  font-weight: 700;
  margin-bottom: 12px;
  text-align: left;
  
  @media (max-width: 350px) {
    font-size: 12px;
  }
`;

const MentorDescription = styled.p`
  font-size: 13px;
  color: ${colors.textSecondary};
  line-height: 1.6;
  text-align: left;
  margin-bottom: 16px;
  flex: 1;
  
  @media (max-width: 350px) {
    font-size: 12px;
    line-height: 1.5;
  }
`;

const TagsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const Tag = styled.span`
  display: inline-block;
  background: ${colors.primaryLight}20;
  color: ${colors.primary};
  padding: 6px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
`;

const NavButton = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 44px;
  height: 44px;
  border-radius: 999px;
  background: white;
  border: 1px solid ${colors.borderPrimary};
  box-shadow: 0 8px 24px rgba(0,0,0,0.12);
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${colors.textPrimary};
  transition: background 0.2s ease;
  z-index: 10;
  cursor: pointer;
  
  &:hover { 
    background: ${colors.backgroundSecondary}; 
  }
  
  &:active {
    transform: translateY(-50%) scale(0.95);
  }
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const PrevButton = styled(NavButton)`
  left: -20px;
`;

const NextButton = styled(NavButton)`
  right: -20px;
`;

const Dots = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
`;

const Dot = styled.button<{ active: boolean }>`
  width: ${p => (p.active ? 28 : 10)}px;
  height: 10px;
  border-radius: 999px;
  background: ${p => (p.active ? colors.primary : colors.borderPrimary)};
  transition: all 200ms ease;
  cursor: pointer;
  border: none;
`;

interface Mentor {
  name: string;
  title: string;
  description: string;
  tags: string[];
  image: string;
  riskStyle: 'conservative' | 'neutral' | 'aggressive';
  timeStyle: 'short' | 'medium' | 'long';
}

const mentors: Mentor[] = [
  {
    name: '워렌 버핏',
    title: '느긋하고 똑똑하게',
    description: '가치투자의 아버지로 불리며, 우수한 기업을 저평가된 가격에 매수하는 전략으로 유명합니다.',
    tags: ['가치투자', '장기투자', '기업분석'],
    image: '/images/mentors/WarrenBuffett.png',
    riskStyle: 'conservative',
    timeStyle: 'long'
  },
  {
    name: '벤자민 그레이엄',
    title: '가치투자의 아버지',
    description: '현대 가치투자 이론의 창시자로, 내재가치와 시장가격의 차이를 통해 수익을 창출하는 방법을 제시했습니다.',
    tags: ['가치투자', '안전마진', '기본분석'],
    image: '/images/mentors/BenjaminGraham.png',
    riskStyle: 'conservative',
    timeStyle: 'medium'
  },
  {
    name: '캐시 우드',
    title: '혁신투자의 여왕',
    description: '파괴적 혁신 기술에 투자하는 성장투자 전문가로, AI, 로보틱스, 에너지 저장 등 미래 기술에 집중합니다.',
    tags: ['성장투자', '혁신기술', 'ETF'],
    image: '/images/mentors/CathieWood.png',
    riskStyle: 'aggressive',
    timeStyle: 'long'
  },
  {
    name: '찰리 멍거',
    title: '버핏의 파트너',
    description: '워렌 버핏의 오랜 동업자로, 심리학과 철학을 투자에 접목한 독창적인 사고방식으로 유명합니다.',
    tags: ['가치투자', '심리학', '다학제적접근'],
    image: '/images/mentors/CharlieMunger.png',
    riskStyle: 'neutral',
    timeStyle: 'long'
  },
  {
    name: '레이 달리오',
    title: '브리지워터 창립자',
    description: '세계 최대 헤지펀드 브리지워터를 창립한 투자자로, 경제 원리와 포트폴리오 분산에 대한 전문가입니다.',
    tags: ['거시경제', '분산투자', '리스크패리티'],
    image: '/images/mentors/RayDalio.png',
    riskStyle: 'neutral',
    timeStyle: 'medium'
  },
  {
    name: '조지 소로스',
    title: '펀드의 제왕',
    description: '재귀성 이론을 바탕으로 한 투기적 투자로 유명하며, 특히 통화 투기를 통해 큰 수익을 올린 투자자입니다.',
    tags: ['거시투자', '통화투기', '재귀성이론'],
    image: '/images/mentors/GeorgeSoros.png',
    riskStyle: 'aggressive',
    timeStyle: 'medium'
  },
  {
    name: '피터 린치',
    title: '월가의 영웅',
    description: '연평균 29%의 수익률을 기록한 전설적인 투자자로, 일반인도 이해하기 쉬운 투자 철학을 제시합니다.',
    tags: ['성장투자', '소비자관점', '일상투자'],
    image: '/images/mentors/PeterLynch.png',
    riskStyle: 'neutral',
    timeStyle: 'short'
  },
  {
    name: '짐 사이먼스',
    title: '퀀트투자의 아버지',
    description: '알고리즘과 데이터 분석을 활용한 퀀트 투자의 선구자입니다. 르네상스 테크놀로지스의 창립자입니다.',
    tags: ['퀀트투자', '알고리즘', '데이터분석'],
    image: '/images/mentors/JimSimons.png',
    riskStyle: 'aggressive',
    timeStyle: 'short'
  },
  {
    name: '마이클 버리',
    title: '빅쇼트의 주인공',
    description: '서브프라임 모기지 사태를 예측하고 큰 수익을 올린 투자자로, 독창적인 분석과 역발상 투자로 유명합니다.',
    tags: ['역발상투자', '시장분석', '위기예측'],
    image: '/images/mentors/MichaelBurry.png',
    riskStyle: 'aggressive',
    timeStyle: 'medium'
  }
];

const getRiskLabel = (style: 'conservative' | 'neutral' | 'aggressive') => {
  switch (style) {
    case 'conservative': return '보수';
    case 'neutral': return '중립';
    case 'aggressive': return '공격';
  }
};

const getTimeLabel = (style: 'short' | 'medium' | 'long') => {
  switch (style) {
    case 'short': return '단기';
    case 'medium': return '중장기';
    case 'long': return '장기';
  }
};

const MentorCard: React.FC<{ mentor: Mentor }> = ({ mentor }) => (
  <>
    <MentorMedia>
      <PortraitFallback>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            fontSize: 'clamp(20px, 5vw, 24px)',
            fontWeight: '700'
          }}>
            {getRiskLabel(mentor.riskStyle)} × {getTimeLabel(mentor.timeStyle)}
          </div>
        </div>
      </PortraitFallback>
    </MentorMedia>
    <MentorBody>
      <MentorName>{mentor.name}</MentorName>
      <MentorTitle>{mentor.title}</MentorTitle>
      <MentorDescription>{mentor.description}</MentorDescription>
      <TagsRow>
        {mentor.tags.map((t, i) => (
          <Tag key={i}>{t}</Tag>
        ))}
      </TagsRow>
    </MentorBody>
  </>
);

export const MentorSection: React.FC = () => {
  const [current, setCurrent] = useState(0);
  const touchStartX = useRef<number | null>(null);

  const total = mentors.length;
  const cardWidth = 320;
  const cardGap = 32;

  const goNext = useCallback(() => {
    setCurrent(prev => (prev + 1) % total);
  }, [total]);

  const goPrev = useCallback(() => {
    setCurrent(prev => (prev - 1 + total) % total);
  }, [total]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') goNext();
      if (e.key === 'ArrowLeft') goPrev();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [goNext, goPrev]);



  // 모바일 스택 카드 위치 계산
  const mobileCards = useMemo(() => {
    return mentors.map((m, index) => {
      const rel = (index - current + total) % total;
      let translateX = 0;
      let translateY = 0;
      let scale = 1;
      let z = 1;
      let opacity = 0;

      if (rel === 0) {
        translateX = 0;
        translateY = 0;
        scale = 1;
        z = 3;
        opacity = 1;
      } else if (rel === 1) {
        translateX = 70;
        translateY = 24;
        scale = 0.95;
        z = 2;
        opacity = 1;
      } else if (rel === total - 1) {
        translateX = -70;
        translateY = 24;
        scale = 0.95;
        z = 2;
        opacity = 1;
      } else {
        translateX = 0;
        translateY = 80;
        scale = 0.9;
        z = 1;
        opacity = 0;
      }

      return { index, rel, translateX, translateY, scale, z, opacity };
    });
  }, [current, total]);

  return (
    <MentorContainer id="mentors" data-section="mentor">
      <MentorContent>
        <SectionTitle>
          <span className="desktop-text">9명의 투자 전설을 만나보세요</span>
          <span className="mobile-text">
            9명의 투자 전설을
            <br />
            만나보세요
          </span>
        </SectionTitle>

        {/* 데스크탑용 가로 스크롤 */}
        <DesktopCarousel>
          <PrevButton onClick={goPrev}>‹</PrevButton>
          <NextButton onClick={goNext}>›</NextButton>
          <DesktopCardsContainer current={current} cardWidth={cardWidth} cardGap={cardGap}>
            {/* 순환 구조를 위해 카드를 3번 복제 */}
            {[...mentors, ...mentors, ...mentors].map((mentor, index) => (
              <DesktopCard key={index}>
                <MentorCard mentor={mentor} />
              </DesktopCard>
            ))}
          </DesktopCardsContainer>
        </DesktopCarousel>

        {/* 모바일용 스택 */}
        <MobileStack>
          <PrevButton onClick={goPrev}>‹</PrevButton>
          <NextButton onClick={goNext}>›</NextButton>
          <MobileCardsStage
            onTouchStart={(e) => { touchStartX.current = e.changedTouches[0].clientX; }}
            onTouchEnd={(e) => {
              if (touchStartX.current == null) return;
              const dx = e.changedTouches[0].clientX - touchStartX.current;
              touchStartX.current = null;
              if (Math.abs(dx) < 40) return;
              if (dx < 0) goNext(); else goPrev();
            }}
          >
            {mobileCards.map(({ index, translateX, translateY, scale, z, opacity }) => {
              const mentor = mentors[index];
              return (
                <MobileCard
                  key={index}
                  translateX={translateX}
                  translateY={translateY}
                  scale={scale}
                  z={z}
                  opacity={opacity}
                >
                  <MentorCard mentor={mentor} />
                </MobileCard>
              );
            })}
          </MobileCardsStage>
        </MobileStack>

        <Dots>
          {mentors.map((_, i) => (
            <Dot key={i} active={i === current} onClick={() => setCurrent(i)} />
          ))}
        </Dots>
      </MentorContent>
    </MentorContainer>
  );
}; 